import { Button } from "@mui/material";
import { Field, FormikProvider, useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { ReactComponent as RightArrowIcon } from "assets/right-arrow.svg";
import { InputField } from "components/InputField";
import { SLAPSHOT_HOME_URL, SLAPSHOT_LOGIN_URL } from "constants/urls";

import styles from "./SlapshotVerify2FA.module.scss";
import { AUTH_TOKEN, AUTH_USER } from "../../constants/constants";
import { verifyLoginOtp } from "../../services/TwoFactorService";

type LocationState = {
  preAuthToken?: string;
  redirectPath?: string;
};

const SlapshotVerify2FAPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as LocationState;
  const [isLoading, setIsLoading] = useState(false);
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);

  useEffect(() => {
    document.title = "Two-Factor Authentication | Slapshot";
  }, []);

  useEffect(() => {
    if (!state.preAuthToken) {
      navigate(SLAPSHOT_LOGIN_URL, { replace: true });
    }
  }, [state.preAuthToken, navigate]);

  const formik = useFormik({
    initialValues: { otp: "" },
    validationSchema: Yup.object({
      otp: Yup.string().trim().required("Code is required"),
    }),
    onSubmit: (values, { setStatus }) => {
      if (!state.preAuthToken) return;
      setIsLoading(true);
      verifyLoginOtp(state.preAuthToken, values.otp.trim())
        .then((res) => {
          localStorage.setItem(AUTH_TOKEN, res.token);
          localStorage.setItem(AUTH_USER, JSON.stringify(res.data));
          navigate(state.redirectPath || SLAPSHOT_HOME_URL);
        })
        .catch((err) => {
          setStatus(err?.detail || "Invalid code");
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
  });

  const isDisabled = isLoading || !!formik.errors.otp || !formik.values.otp;

  return (
    <FormikProvider value={formik}>
      <form className={styles.container} onSubmit={formik.handleSubmit}>
        <div className={styles.welcomeContainer}>
          <h3>Two-factor authentication</h3>
          <p>
            {useRecoveryCode
              ? "Enter one of your saved recovery codes."
              : "Enter the 6-digit code from your authenticator app."}
          </p>
        </div>
        <div className={styles.formContainer}>
          {formik.status && (
            <div className={styles.formError}>{formik.status}</div>
          )}
          <div className={styles.inputContainer}>
            <Field
              name="otp"
              component={InputField}
              label={useRecoveryCode ? "Recovery code" : "Authentication code"}
              placeholder={useRecoveryCode ? "abcd1234ef" : "123456"}
              autoComplete="one-time-code"
              autoFocus
            />
            <button
              type="button"
              className={styles.toggleRecovery}
              onClick={() => {
                setUseRecoveryCode((v) => !v);
                formik.setFieldValue("otp", "");
                formik.setStatus(undefined);
              }}
            >
              {useRecoveryCode
                ? "Use your authenticator app instead"
                : "Use a recovery code instead"}
            </button>
          </div>
        </div>
        <Button
          type="submit"
          disabled={isDisabled}
          className="slapshotAuthButton"
        >
          Verify
          <RightArrowIcon style={{ marginLeft: "12px" }} />
        </Button>
      </form>
    </FormikProvider>
  );
};

export default SlapshotVerify2FAPage;
